import { useState, useCallback } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const usePayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get payment intent for a listing (used for authenticated users)
  const getPaymentIntent = useCallback(async (listingId) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(
        `${API_BASE_URL}/listings/${listingId}/payment-intent`,
        {
          headers,
        }
      );
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to get payment intent";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get payment intent for guest listing by management token
  const getGuestPaymentIntent = useCallback(async (managementToken) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/listings/guest/${managementToken}/payment-intent`
      );
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to get payment intent";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Confirm payment after successful Stripe payment
  const confirmPayment = useCallback(async (listingId, paymentIntentId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/payments/payments/confirm`,
        {
          listing_id: listingId,
          payment_intent_id: paymentIntentId,
        }
      );
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to confirm payment";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Confirm payment for guest listing
  const confirmGuestPayment = useCallback(
    async (managementToken, paymentIntentId) => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.post(
          `${API_BASE_URL}/payments/confirm-guest`,
          {
            management_token: managementToken,
            payment_intent_id: paymentIntentId,
          }
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          err.response?.data?.detail || "Failed to confirm payment";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Get payment status for a listing
  const getPaymentStatus = useCallback(async (listingId) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API_BASE_URL}/listings/${listingId}/payment-status`,
        {
          headers,
        }
      );

      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to get payment status";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get payment status for guest listing
  const getGuestPaymentStatus = useCallback(async (managementToken) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/listings/guest/${managementToken}/payment-status`
      );
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to get payment status";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Process refund for a payment
  const processRefund = useCallback(async (paymentId, amount = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/payments/${paymentId}/refund`,
        {
          amount: amount,
        }
      );
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to process refund";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get payment history for a user (requires authentication)
  const getPaymentHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/payments/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Failed to get payment history";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getPaymentIntent,
    getGuestPaymentIntent,
    confirmPayment,
    confirmGuestPayment,
    getPaymentStatus,
    getGuestPaymentStatus,
    processRefund,
    getPaymentHistory,
  };
};
